// -----------------------------------------------------------
// FloraIQ - SurveySection — Aba Levantamentos
// -----------------------------------------------------------

import { useState, useMemo } from "react";
import { surveys, surveyTypes, surveyStatuses } from "@/mocks/pointcloud";
import ProcessingBadge, { QualityBadge } from "./PointCloudStatusBadge";

export default function SurveySection() {
  const [search, setSearch] = useState("");
  const [filterProjeto, setFilterProjeto] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  const projetos = useMemo(() => [...new Set(surveys.map((s) => s.projeto))], []);

  const filtered = useMemo(() => {
    return surveys.filter((s) => {
      if (search && !s.nome.toLowerCase().includes(search.toLowerCase()) && !s.municipio.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterProjeto && s.projeto !== filterProjeto) return false;
      if (filterTipo && s.tipo !== filterTipo) return false;
      if (filterStatus && s.status !== filterStatus) return false;
      return true;
    });
  }, [search, filterProjeto, filterTipo, filterStatus]);

  const hasFilters = search || filterProjeto || filterTipo || filterStatus;

  const clearFilters = () => {
    setSearch("");
    setFilterProjeto("");
    setFilterTipo("");
    setFilterStatus("");
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nome, município ou responsável..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-background-200/70 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white"
          />
        </div>
        <select value={filterProjeto} onChange={(e) => setFilterProjeto(e.target.value)} className="px-3 py-2 text-sm border border-background-200/70 rounded-md bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400">
          <option value="">Todos os projetos</option>
          {projetos.map((p) => <option key={p}>{p}</option>)}
        </select>
        <select value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)} className="px-3 py-2 text-sm border border-background-200/70 rounded-md bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400">
          <option value="">Todos os tipos</option>
          {surveyTypes.map((t) => <option key={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-background-200/70 rounded-md bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400">
          <option value="">Todos os status</option>
          {surveyStatuses.map((s) => {
            const labels: Record<string, string> = { planejado: "Planejado", coletado: "Coletado", em_processamento: "Em Processamento", processado: "Processado", validado: "Validado", com_inconsistencia: "Com Inconsistência", arquivado: "Arquivado" };
            return <option key={s} value={s}>{labels[s]}</option>;
          })}
        </select>
        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-primary-500 hover:text-primary-600 cursor-pointer px-2 py-2 whitespace-nowrap">
            <i className="ri-close-line mr-1"></i>Limpar filtros
          </button>
        )}
        <div className="flex items-center gap-1 ml-auto bg-background-100 rounded-lg p-0.5">
          <button onClick={() => setViewMode("table")} className={`px-2.5 py-1.5 text-xs rounded-md cursor-pointer transition-colors ${viewMode === "table" ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-500"}`}>
            <i className="ri-list-check mr-1"></i>Tabela
          </button>
          <button onClick={() => setViewMode("cards")} className={`px-2.5 py-1.5 text-xs rounded-md cursor-pointer transition-colors ${viewMode === "cards" ? "bg-white text-foreground-900 shadow-sm" : "text-foreground-500"}`}>
            <i className="ri-layout-grid-line mr-1"></i>Cards
          </button>
        </div>
      </div>

      {/* Resultados */}
      <p className="text-xs text-foreground-500">{filtered.length} de {surveys.length} levantamentos</p>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-search-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Nenhum levantamento encontrado com os filtros atuais</p>
        </div>
      ) : viewMode === "table" ? (
        <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-50/50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Levantamento</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Tipo</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Projeto</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Município</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Data</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Equipamento</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Área (ha)</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Pontos</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Status</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Qualidade</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-b border-background-100/50 hover:bg-background-50/30 transition-colors">
                    <td className="px-4 py-2.5 text-xs font-medium text-foreground-800 whitespace-nowrap">{s.nome}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-600 whitespace-nowrap">{s.tipo}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-600 whitespace-nowrap">{s.projeto}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-500 whitespace-nowrap">{s.municipio}/{s.uf}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-600 whitespace-nowrap">{new Date(s.data + "T00:00:00").toLocaleDateString("pt-BR")}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-500 max-w-[140px] truncate">{s.equipamento}</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-600 whitespace-nowrap">{s.areaHa} ha</td>
                    <td className="px-4 py-2.5 text-xs text-foreground-600 whitespace-nowrap font-mono">{s.pontosEstimados}</td>
                    <td className="px-4 py-2.5"><ProcessingBadge status={s.status} /></td>
                    <td className="px-4 py-2.5"><QualityBadge value={s.qualidade} /></td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer" title="Visualizar" aria-label="Visualizar">
                          <i className="ri-eye-line text-foreground-500 text-sm"></i>
                        </button>
                        <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer" title="Vincular árvores" aria-label="Vincular árvores">
                          <i className="ri-link text-foreground-500 text-sm"></i>
                        </button>
                        <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer" title="Abrir 3D" aria-label="Abrir 3D">
                          <i className="ri-stack-line text-foreground-500 text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((s) => (
            <div key={s.id} className="bg-white rounded-card border border-background-200/70 p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h5 className="text-sm font-bold text-foreground-900 font-heading truncate">{s.nome}</h5>
                  <p className="text-xs text-foreground-500 mt-0.5">{s.tipo}</p>
                </div>
                <ProcessingBadge status={s.status} />
              </div>
              <div className="space-y-1.5 text-xs text-foreground-600">
                <div className="flex justify-between"><span className="text-foreground-400">Projeto</span><span className="font-medium">{s.projeto}</span></div>
                <div className="flex justify-between"><span className="text-foreground-400">Data</span><span>{new Date(s.data + "T00:00:00").toLocaleDateString("pt-BR")}</span></div>
                <div className="flex justify-between"><span className="text-foreground-400">Área</span><span className="font-medium">{s.areaHa} ha</span></div>
                <div className="flex justify-between"><span className="text-foreground-400">Árvores</span><span className="font-medium">{s.arvoresVinculadas}</span></div>
                <div className="flex justify-between"><span className="text-foreground-400">Qualidade</span><QualityBadge value={s.qualidade} /></div>
              </div>
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-background-100/50">
                <button className="flex-1 text-xs py-1.5 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1">
                  <i className="ri-eye-line text-xs"></i>Visualizar
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer" title="Abrir 3D" aria-label="Abrir 3D">
                  <i className="ri-stack-line text-foreground-500 text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}